import { CalculationFunction } from '../calculationMetadataTypes';
import { AddedMetric } from 'src/reducers/customMetrices';

interface ValidationResult {
    valid: boolean;
    error?: string;
    suggestions?: string[];
}

interface FieldInfo {
    prettyName: string;
    type?: string;
    source?: 'backend' | 'custom';
}

export const extractFieldReferences = (sql: string): string[] => {
    const referencePattern = /\[\[([^\]]+)\]\]/g;
    const references: string[] = [];
    let match;

    while ((match = referencePattern.exec(sql)) !== null) {
        references.push(match[1]);
    }

    return references;
};

export const validateFieldReferences = (
    sql: string,
    availableFields: FieldInfo[],
): ValidationResult => {
    const references = extractFieldReferences(sql);

    if (references.length === 0) {
        return { valid: true };
    }

    const invalidReferences = references.filter((refName) => {
        return !availableFields.some((field) => field.prettyName === refName);
    });

    if (invalidReferences.length > 0) {
        const suggestions = invalidReferences
            .map((invalid) => {
                const similar = availableFields.find(
                    (field) =>
                        field.prettyName.toLowerCase().includes(invalid.toLowerCase()) ||
                        invalid.toLowerCase().includes(field.prettyName.toLowerCase()),
                );
                return similar ? `Did you mean [[${similar.prettyName}]]?` : null;
            })
            .filter(Boolean) as string[];

        return {
            valid: false,
            error: `Field reference not found: ${invalidReferences.map((r) => `[[${r}]]`).join(', ')}`,
            suggestions,
        };
    }

    return { valid: true };
};

export const findDependentMetrics = (
    targetMetricName: string,
    allMetrics: AddedMetric[],
): AddedMetric[] => {
    return allMetrics.filter((metric) => {
        const references = extractFieldReferences(metric.sql);
        return references.includes(targetMetricName);
    });
};

export const validateMetricDeletion = (
    metricToDelete: AddedMetric,
    allMetrics: AddedMetric[],
): { canDelete: boolean; dependents: AddedMetric[] } => {
    const dependents = findDependentMetrics(metricToDelete.prettyName, allMetrics);
    return {
        canDelete: dependents.length === 0,
        dependents,
    };
};

export const validateCalculationFunctions = (
    sql: string,
    functionMetadata: CalculationFunction[],
): ValidationResult => {
    const functionPattern = /(BI_[A-Z_]+)\s*\(/g;
    const errors: string[] = [];
    let match;

    while ((match = functionPattern.exec(sql)) !== null) {
        const functionName = match[1];

        const metadata = functionMetadata.find((f) => f.name === functionName);

        if (!metadata) {
            if (functionMetadata.length === 0) {
                continue;
            }
            errors.push(`Unknown function: ${functionName}`);
            continue;
        }

        const functionStartIndex = match.index;
        const paramsMatch = sql.substring(functionStartIndex).match(/\(([^)]*)\)/);

        if (paramsMatch) {
            const paramsStr = paramsMatch[1];
            const params = splitParams(paramsStr);

            const requiredParams = metadata.parameters.filter((p) => p.type !== 'variadic');
            const isVariadic = metadata.parameters.some((p) => p.type === 'variadic');

            if (!isVariadic && params.length !== metadata.parameters.length) {
                errors.push(
                    `${functionName} expects ${metadata.parameters.length} parameters, got ${params.length}`,
                );
            } else if (isVariadic && params.length < requiredParams.length) {
                errors.push(`${functionName} requires at least ${requiredParams.length} parameter(s)`);
            }
        }
    }

    if (errors.length > 0) {
        return {
            valid: false,
            error: errors.join('; '),
        };
    }

    return { valid: true };
};

const splitParams = (paramsStr: string): string[] => {
    const params: string[] = [];
    let current = '';
    let depth = 0;
    let inBracket = false;

    for (const char of paramsStr) {
        if (char === '(' || char === '[') {
            depth++;
            if (char === '[') inBracket = true;
        } else if (char === ')' || char === ']') {
            depth--;
            if (char === ']') inBracket = false;
        } else if (char === ',' && depth === 0 && !inBracket) {
            params.push(current.trim());
            current = '';
            continue;
        }
        current += char;
    }

    if (current.trim()) {
        params.push(current.trim());
    }

    return params.filter((p) => p);
};

/**
 * Validate SQL syntax (basic validation)
 * Checks for common syntax errors
 */
export const validateSQLSyntax = (sql: string): ValidationResult => {
    const trimmedSql = sql.trim();

    if (!trimmedSql) {
        return {
            valid: false,
            error: 'SQL expression cannot be empty',
        };
    }

    let parenCount = 0;
    for (const char of trimmedSql) {
        if (char === '(') parenCount++;
        if (char === ')') parenCount--;
        if (parenCount < 0) {
            return {
                valid: false,
                error: 'Unbalanced parentheses: extra closing parenthesis',
                suggestions: ['Check for extra ) characters'],
            };
        }
    }

    if (parenCount !== 0) {
        return {
            valid: false,
            error: 'Unbalanced parentheses: missing closing parenthesis',
            suggestions: ['Check for missing ) characters'],
        };
    }

    // Check for balanced brackets [[ and ]]
    const openBrackets = (trimmedSql.match(/\[\[/g) || []).length;
    const closeBrackets = (trimmedSql.match(/\]\]/g) || []).length;

    if (openBrackets !== closeBrackets) {
        return {
            valid: false,
            error: 'Unbalanced field reference brackets',
            suggestions: ['Field references should use [[FieldName]] format'],
        };
    }

    // Check for unclosed quotes
    const singleQuotes = (trimmedSql.match(/'/g) || []).length;
    const doubleQuotes = (trimmedSql.match(/"/g) || []).length;

    if (singleQuotes % 2 !== 0) {
        return {
            valid: false,
            error: 'Unclosed single quote',
            suggestions: ['Check for missing closing single quote'],
        };
    }

    if (doubleQuotes % 2 !== 0) {
        return {
            valid: false,
            error: 'Unclosed double quote',
            suggestions: ['Check for missing closing double quote'],
        };
    }

    return { valid: true };
};

/**
 * Validate unique metric name
 */
export const validateUniqueName = (
    prettyName: string,
    existingNames: string[],
): ValidationResult => {
    if (!prettyName || !prettyName.trim()) {
        return {
            valid: false,
            error: 'Metric name is required',
        };
    }

    if (existingNames.includes(prettyName)) {
        return {
            valid: false,
            error: `Metric name "${prettyName}" already exists`,
        };
    }

    return { valid: true };
};

/**
 * Main validation function
 * Runs all validations in sequence
 */
export const validateCustomMetric = (
    sql: string,
    prettyName: string,
    availableFields: FieldInfo[],
    calculationFunctions: CalculationFunction[],
    existingMetricNames: string[],
): ValidationResult => {
    const nameValidation = validateUniqueName(prettyName, existingMetricNames);
    if (!nameValidation.valid) {
        return nameValidation;
    }

    const syntaxValidation = validateSQLSyntax(sql);
    if (!syntaxValidation.valid) {
        return syntaxValidation;
    }

    const fieldValidation = validateFieldReferences(sql, availableFields);
    if (!fieldValidation.valid) {
        return fieldValidation;
    }

    if (calculationFunctions && calculationFunctions.length > 0) {
        const functionValidation = validateCalculationFunctions(sql, calculationFunctions);
        if (!functionValidation.valid) {
            return functionValidation;
        }
    }

    return { valid: true };
};
